import _ from 'lodash'
import moment from 'moment'
import { v4 as uuidv4 } from 'uuid'

import { EventType } from 'shared-libs/models/types/storyboard/storyboard-execution'

// Pure activity-feed grouping logic, extracted from the Feed component so it can be
// unit-tested without loading the whole component tree (Blueprint, mapbox, the component
// registry). See .claude/docs/activity-feed-event-grouping.md for the mechanism and the
// passive-event fold landmine.

export type TaskGroupKeyInfo = {
  key: string
  author: string
  scope: 'task' | 'scene'
  storyId?: string
  sceneId?: string
  taskId?: string
}

export type TaskGroupMeta = TaskGroupKeyInfo & {
  lastEventDate?: string
  eventTypes: EventType[]
  events: any[]
}

const taskGroupableEventTypes = new Set([
  EventType.ACTION,
  EventType.BREADCRUMB,
  EventType.COMPUTATION,
  EventType.EDIT,
  EventType.SYNTHETIC,
  EventType.MAP_APPOINTMENT,
  EventType.TRAILER_LOOKUP_RESPONSE,
  EventType.NOTIFICATION,
  EventType.NOTIFICATION_LOG,
])
const passiveEventTypes = new Set([
  EventType.SYNTHETIC,
  EventType.MAP_APPOINTMENT,
  EventType.TRAILER_LOOKUP_RESPONSE,
])
const taskGroupWindowMs = 10 * 60 * 1000

export function groupWorkflowEventsByTask(events) {
  if (_.isEmpty(events)) {
    return events
  }

  const orderedEvents = _.orderBy(events, (event) => Date.parse(event.creationDate) || 0, 'asc')
  const groupedEvents = []
  let activeGroup = null

  orderedEvents.forEach((event) => {
    const groupInfo = getTaskGroupInfo(event)
    if (groupInfo) {
      if (!activeGroup || !canAppendToTaskGroup(activeGroup, event, groupInfo.key)) {
        activeGroup = createTaskGroupEvent(event, groupInfo)
        groupedEvents.push(activeGroup)
      } else {
        appendEventToTaskGroup(activeGroup, event)
      }
      return
    }

    if (shouldAppendPassiveEventToActiveGroup(event, activeGroup)) {
      appendEventToTaskGroup(activeGroup, event)
      return
    }

    groupedEvents.push(event)
    activeGroup = null
  })
  return groupedEvents.filter(shouldIncludeGroupedEvent)
}

function getTaskGroupInfo(event): TaskGroupKeyInfo | undefined {
  if (!event || !taskGroupableEventTypes.has(event.eventType)) {
    return undefined
  }

  const { source, createdBy } = event
  if (!source) {
    return undefined
  }
  const { storyId, sceneId, taskId } = source
  const hasTask = !!taskId
  const hasScene = !!sceneId
  if (!hasTask && !hasScene) {
    return undefined
  }

  const scope: 'task' | 'scene' = hasTask ? 'task' : 'scene'
  const pathParts = _.compact([storyId, sceneId, hasTask ? taskId : undefined])
  const workflowPath = pathParts.join(':')
  if (!workflowPath) {
    return undefined
  }

  const author = createdBy?.entityId || 'unknown'
  return {
    key: `${author}:${workflowPath}`,
    author,
    scope,
    storyId,
    sceneId,
    taskId,
  }
}

function createTaskGroupEvent(event, groupInfo: TaskGroupKeyInfo) {
  const groupedEvent = _.cloneDeep(event)
  const baseId = event?.id || uuidv4()
  groupedEvent.id = `task-group-${baseId}`
  groupedEvent.eventType = EventType.TASK_GROUP
  groupedEvent.outputMappings = getGroupableOutputMappings(event)
  groupedEvent.taskGroup = {
    ...groupInfo,
    eventTypes: [event.eventType],
    events: [event],
    lastEventDate: event.creationDate,
  } as TaskGroupMeta

  return groupedEvent
}

function canAppendToTaskGroup(groupEvent, event, groupKey) {
  const taskGroup: TaskGroupMeta = groupEvent?.taskGroup
  if (!taskGroup || taskGroup.key !== groupKey) {
    return false
  }

  return isWithinGroupingWindow(taskGroup.lastEventDate, event?.creationDate)
}

function shouldAppendPassiveEventToActiveGroup(event, activeGroup) {
  if (!activeGroup || !passiveEventTypes.has(event?.eventType)) {
    return false
  }

  const taskGroup: TaskGroupMeta = activeGroup.taskGroup
  if (!taskGroup) {
    return false
  }

  const author = event?.createdBy?.entityId || 'unknown'
  if (author !== taskGroup.author) {
    return false
  }

  return isWithinGroupingWindow(taskGroup.lastEventDate, event?.creationDate)
}

function isWithinGroupingWindow(previousDate?: string, nextDate?: string) {
  if (!previousDate || !nextDate) {
    return true
  }

  const lastMoment = moment(previousDate)
  const nextMoment = moment(nextDate)
  return Math.abs(lastMoment.diff(nextMoment)) <= taskGroupWindowMs
}

function appendEventToTaskGroup(groupEvent, event) {
  const taskGroup: TaskGroupMeta = groupEvent.taskGroup
  if (!taskGroup) {
    return
  }

  taskGroup.events.push(event)
  taskGroup.eventTypes = _.uniq(taskGroup.eventTypes.concat(event.eventType))
  taskGroup.lastEventDate = getLatestTimestamp(taskGroup.lastEventDate, event.creationDate)

  const nextMappings = getGroupableOutputMappings(event)
  if (!_.isEmpty(nextMappings)) {
    groupEvent.outputMappings = (groupEvent.outputMappings || []).concat(nextMappings)
  }

  groupEvent.creationDate = getLatestTimestamp(groupEvent.creationDate, event.creationDate) || groupEvent.creationDate
  groupEvent.processedDate = getLatestTimestamp(groupEvent.processedDate, event.processedDate) || groupEvent.processedDate

  if (!groupEvent.deviceInfo && event.deviceInfo) {
    groupEvent.deviceInfo = event.deviceInfo
  }
  if (!groupEvent.createdBy && event.createdBy) {
    groupEvent.createdBy = event.createdBy
  }
}

function shouldIncludeGroupedEvent(event) {
  if (event?.eventType !== EventType.TASK_GROUP) {
    return true
  }

  const taskGroup: TaskGroupMeta = event.taskGroup
  if (!taskGroup) {
    return true
  }

  const hasDisplayableEvent = (taskGroup.events || []).some((groupEvent) =>
    doesEventHaveDisplayableChange(groupEvent)
  )
  const hasSyntheticWithMappings = taskGroup.events?.some(
    (groupEvent) =>
      groupEvent?.eventType === EventType.SYNTHETIC &&
      !_.isEmpty(getDisplayableMappings(groupEvent))
  )
  const hasOutputMappings = !_.isEmpty(event.outputMappings)

  return hasDisplayableEvent || hasSyntheticWithMappings || hasOutputMappings
}

function doesEventHaveDisplayableChange(event) {
  if (!event) {
    return false
  }

  if (
    [EventType.NOTIFICATION, EventType.NOTIFICATION_LOG].includes(event.eventType) ||
    passiveEventTypes.has(event.eventType)
  ) {
    return true
  }

  if (event.eventType === EventType.BREADCRUMB) {
    return false
  }

  const displayableMappings = getDisplayableMappings(event)
  return !_.isEmpty(displayableMappings)
}

function getGroupableOutputMappings(event) {
  if (event?.eventType === EventType.BREADCRUMB) {
    return []
  }

  return getDisplayableMappings(event)
}

function getDisplayableMappings(event) {
  const mappings = _.cloneDeep(event?.outputMappings) || []
  return mappings.filter((mapping) => !shouldIgnoreMapping(mapping))
}

function shouldIgnoreMapping(mapping) {
  const destination = mapping?.destination || ''
  return destination.includes('sharedContext.')
}

function getLatestTimestamp(current, next) {
  if (!current) {
    return next
  }
  if (!next) {
    return current
  }

  const currentMoment = moment(current)
  const nextMoment = moment(next)
  return currentMoment.isAfter(nextMoment) ? current : next
}
